import { useCallback, useMemo } from 'react';
import RadiosIcon from '@ubnt/icons/RadiosIcon';
import { RadioRolesEnum } from '@ubnt/link-core/enums';
import { Popover } from '@ubnt/ui-components/aria/Core/Popover';
import OptionList from '@ubnt/ui-components/OptionList/OptionList';
import { useDevicePanel } from '@ubnt/link-core-ui/components/BottomPanel/context/DevicePanelContext';
import { ButtonIcon } from '@ubnt/ui-components/aria/experimental/Button';
import { Tooltip_Default } from '@ubnt/ui-components/aria/experimental/Tooltip';
import { trackEvent } from 'utils/googleAnalytics';
import { useDispatch, useSelector } from 'app/store';
import {
  devicesDeviceAntennaNameSelector,
  devicesDeviceInstanceSelector,
  devicesDeviceRoleSelector,
  devicesDeviceUpdate,
} from 'modules/devices/slice';

import classes from 'modules/bottomPanel/components/compositions/DevicePanel.module.scss';
import COLORS from 'styles/colors.module.scss';

const ChangeAntennaButton = () => {
  const { id } = useDevicePanel();

  const dispatch = useDispatch();
  const role = useSelector((state) => (id ? devicesDeviceRoleSelector(state, id) : undefined));
  const apDevice = useSelector((state) => (id ? devicesDeviceInstanceSelector(state, id) : undefined));
  const antennaName = useSelector((state) => (id ? devicesDeviceAntennaNameSelector(state, id) : undefined));

  const onSelect = useCallback(
    (close?: () => void) => (value: string) => {
      trackEvent('bottomPanelAntennaButton');
      if (id) {
        dispatch(devicesDeviceUpdate({ id, changes: { antennaName: value } }));
      }
      close?.();
    },
    [dispatch, id]
  );

  const options = useMemo(() => {
    if (role !== RadioRolesEnum.Ap || !apDevice || apDevice.antennas.length < 2) {
      return null;
    }
    return apDevice.antennas.map(({ name }) => {
      return {
        label: name,
        value: name,
        selected: name === antennaName,
      };
    });
  }, [role, apDevice, antennaName]);

  if (!options) {
    return null;
  }

  return (
    <Popover.Root>
      <Popover.Trigger>
        <Tooltip_Default description="Click to change device antenna type" offset={10} title={antennaName}>
          <ButtonIcon variant="neutral" size="small">
            <RadiosIcon color={COLORS.neutral08} size="medium" />
          </ButtonIcon>
        </Tooltip_Default>
      </Popover.Trigger>
      <Popover.Content placement="bottom start" width="auto">
        {({ close }) => (
          <OptionList
            className={classes.optionList}
            selected={antennaName}
            onSelect={onSelect(close)}
            options={options}
          />
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export default ChangeAntennaButton;
